import { Space } from "@prisma/client";
import { Link, redirect, useLoaderData, useSearchParams } from "@remix-run/react";
import { type ActionFunctionArgs, type LoaderFunctionArgs, MetaFunction } from "@vercel/remix";
import { ArrowLeftIcon, XIcon } from "lucide-react";

import { Badge, Button, DialogHeader, DialogTitle, Modal } from "~/components";
import { SpaceLocation } from "~/components/shared/Space";
import { CancelIcon, EditIcon } from "~/icons";
import { currency } from "~/lib/utils";
import { addSpaceToCart, getCartFromCookie, getCustomerFromCookie, getOrCreateCartFromCookie } from "~/models";
import { db } from "~/models/db.server";

import { cryptLocations } from "./FloorPlan";
import Map from "./Map";
import { SelectedSpace } from "./SelectedSpace";
import {
    APPLY_FILTERS,
    ProductFiltersForm,
    RESET_FILTERS,
    type SearchParamFilters,
    filterSchema,
} from "./SpaceFilters";
import { Spaces } from "./Spaces";

import DATA from "~/data";
import { analyticsServer } from "~/utils/analytics.server";

export const meta: MetaFunction = () => {
    return [
        { title: "Map | Our Guidant" },
        { name: "description", content: "Choose available memorial spaces for your loved one" },
    ];
};


export async function loader({ request }: LoaderFunctionArgs) {
    const referer = request.headers.get("referer");

    const analytics = analyticsServer(request);

    if (referer?.includes("/packages")) {
        analytics.track("Immediate Need - Map Page Viewed", {
            referer,
        });
    }

    if (referer?.includes("/plan-for-the-future")) {
        analytics.track("5. Plan for the Future - Map Page Viewed", {
            referer,
        });
    }
    const spaces = await db.space.findMany({
        where: {
            // salesStatus: "Un-Sold",
            // isValid: true,
            vendorId: DATA.vendor.id,
        },
        include: {
            intermentRights: true,
        },
    });

    const prices = spaces.map(({ price }) => price);
    const intermentRightsLength = spaces.map(({ intermentRights }) => intermentRights.length);

    const cart = await getCartFromCookie({ request });
    const { customer, withAuthKit } = await getCustomerFromCookie({ request });
    /*
     * FIXME use cartId from session to fetch package info?
     */

    return {
        spaces,
        intermentRightsRange: {
            min: 2,
            max: Math.max(...intermentRightsLength),
        },
        priceRange: {
            min: Math.min(...prices) / 100,
            max: (Math.max(...prices) + 1000 * 100) / 100,
        },
        cart,
        baseUrl: process.env.BASE_URL,
        customer,
        withAuthKit,
        referer,
    };
}

export default function MapPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const spaceId = searchParams.get("spaceId");

    const { intermentRightsRange, priceRange, spaces, cart, baseUrl, customer, withAuthKit } =
        useLoaderData<typeof loader>();
    const selectedSpace = spaces.find((space) => space.lotSellUnitId === spaceId);

    function selectSpace(space) {
        if (space) {
            searchParams.set("spaceId", space.lotSellUnitId);
        } else {
            searchParams.delete("spaceId");
        }
        setSearchParams(searchParams, { preventScrollReset: true });
    }

    const filters = filterSchema.parse(Object.fromEntries(searchParams));

    const filteredSpaces = spaces.map((space) => {
        const isFiltered = [
            "intermentRights",
            "minimumPrice",
            "maximumPrice",
            "groundLevel",
            "mausoleumId",
            "crypt",
            "burialType",
            "section",
        ]
            .filter((filter) => !!filters[filter])
            .every((key) => matches[key](space, filters));

        return {
            ...space,
            isFiltered,
        };
    });

    const mausoleumSelected = searchParams.get("mausoleumId") || searchParams.get("crypt");
    const selectedCrypt = searchParams.get("crypt");

    const goBackFromMausoleum = () => {
        if (selectedCrypt === null) {
            searchParams.delete("mausoleumId");
        }
        searchParams.delete("spaceId");
        searchParams.delete("crypt");
        setSearchParams(searchParams);
    };

    // If selectedSpace scroll to top
    // SAVE FOR LATER JUMPING HERE
    if (selectedSpace) {
        if (typeof window !== "undefined") {
            window.scrollTo(0, 0);
        }
    }

    let mausoleumSectionName = searchParams.get("mausoleumId");
    const crypt = searchParams.get("crypt");

    if (mausoleumSectionName && crypt) {
        mausoleumSectionName = `${mausoleumSectionName} ${crypt}`;
    }

    return (
        <>
            <div className="flex flex-col md:flex-row">
                <div className="flex-1 z-40 sticky top-0 self-start border-0 w-full">
                    {/* ! necessary because height is defined by mapbox*/}
                    <div className="absolute w-full h-full top-0 left-0">
                        <img src={DATA.map.background.src} alt={DATA.map.background.alt} className="w-full h-full object-cover" />
                        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-70" />
                    </div>
                    <Map
                        className="!h-48 md:!h-screen"
                        spaces={filteredSpaces}
                        selectSpace={selectSpace}
                        selectedSpace={selectedSpace}
                    />
                </div>
                <div className="flex-1 px-page md:px-8 pb-8 pt-8 md:pt-0 flex flex-col gap-6 max-w-[1000px]">
                    {selectedSpace ? (
                        <>
                            <div className="flex flex-col gap-2">
                                <Button
                                    variant="action"
                                    onClick={() => selectSpace(null)}
                                    className="flex self-start items-center gap-2 hover:underline md:pt-0"
                                >
                                    <ArrowLeftIcon className="w-5 h-5" />
                                    Back
                                </Button>
                                <h1 className="text-xl md:text-3xl font-title">
                                    {mausoleumSectionName ? mausoleumSectionName : selectedSpace?.section}
                                </h1>
                            </div>
                            {/* <div>TITLE</div> */}
                            <SelectedSpace
                                cart={cart}
                                space={selectedSpace}
                                baseUrl={baseUrl}
                                customer={customer}
                                withAuthKit={withAuthKit}
                            />
                            <p className="muted text-sm">
                                All spaces include the right for two interments, even if you are purchasing for only one
                                person.
                            </p>
                        </>
                    ) : (
                        <>
                            <div className="flex flex-col gap-4">
                                {!mausoleumSelected ? (
                                    <div className="flex gap-8 justify-between">
                                        {searchParams.get("fromQuestionnaire") === "true" ? (
                                            <div className="flex flex-col gap-2">
                                                <h1 className="text-3xl font-title">Recommended Memorial Spaces</h1>
                                                <h2>
                                                    Based on your answers, these are the options we think would be most
                                                    suitable for you.
                                                </h2>
                                            </div>
                                        ) : (
                                            <h1 className="text-xl sm:text-3xl md:text-xl lg:text-3xl font-title">
                                                Available Memorial Spaces
                                            </h1>
                                        )}
                                        <Modal
                                            open={!!searchParams.get("filters")}
                                            onOpenChange={(state: boolean) => {
                                                if (state) {
                                                    searchParams.set("filters", "true");
                                                } else {
                                                    searchParams.delete("filters");
                                                }
                                                setSearchParams(searchParams);
                                            }}
                                            trigger={`Filters (${Object.keys(filters).filter((key) => getFilterBadgeLabel(filters, key))
                                                .length
                                                })`}
                                            buttonClass="bg-white text-black uppercase hover:text-white hover:bg-black border-2 border-black"
                                            variant="outline"
                                        >
                                            <DialogHeader>
                                                <DialogTitle>Filters</DialogTitle>
                                            </DialogHeader>
                                            <ProductFiltersForm
                                                filters={filters}
                                                priceRange={priceRange}
                                                intermentRightsRange={intermentRightsRange}
                                            />
                                        </Modal>
                                    </div>
                                ) : (
                                    mausoleumSelected && (
                                        <div className="flex flex-col gap-2">
                                            <Button
                                                variant="action"
                                                className="flex self-start items-center gap-2 hover:underline"
                                                onClick={goBackFromMausoleum}
                                            >
                                                <ArrowLeftIcon className="w-5 h-5" />
                                                Back
                                            </Button>
                                            <h1 className="text-xl md:text-3xl font-title">{mausoleumSectionName}</h1>
                                        </div>
                                    )
                                )}
                                <SelectedFilters filters={filters} />
                            </div>
                            <Spaces
                                spaces={filteredSpaces.filter((space) => space.salesStatus === "Un-Sold")}
                                onSpaceClick={selectSpace}
                            />
                        </>
                    )}
                </div>
            </div>
            {searchParams.get("cartItemAdded") && !!cart ? <SpaceBanner cart={cart} /> : null}
            {searchParams.get("packageAdded") && !!cart ? (
                <CremationPackageBanner slug={searchParams.get("packageAdded")} cart={cart} />
            ) : null}
        </>
    );
}

function SpaceBanner({ cart }) {
    // latest space
    const { space } = cart?.spaces[cart?.spaces?.length - 1];
    const [searchParams, setSearchParams] = useSearchParams();
    function closeBanner() {
        searchParams.delete("cartItemAdded");
        setSearchParams(searchParams, { preventScrollReset: true });
    }

    const numCartItems = cart?.spaces?.length;
    let andMoreItemsText = "";
    if (cart?.spaces?.length > 1) {
        andMoreItemsText = `and ${numCartItems} other items are in your cart`;
    }
    if (!space) {
        return null;
    }

    return (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 max-w-2xl w-full z-50 px-2 md:px-0">
            <section className="relative flex flex-col gap-2 md:flex-row justify-between w-full mx-auto px-5 py-3 bg-white shadow-2xl rounded-md border border-border">
                <div className="flex-1 space-y-1">
                    <h2 className="capitalize font-semibold text-base">
                        {space.section}
                        {numCartItems > 1 && (
                            <span className="ml-1">
                                - <SpaceLocation {...space} />
                            </span>
                        )}
                    </h2>
                    <section className="flex flex-row-reverse justify-end gap-2 text-[15px]">
                        {andMoreItemsText ? (
                            <div>{andMoreItemsText}</div>
                        ) : (
                            <div>
                                <SpaceLocation {...space} />
                                <span className="ml-1">added to your cart</span>
                            </div>
                        )}
                    </section>
                </div>
                <div className="flex items-center md:px-4">
                    <button onClick={closeBanner} className="absolute top-2 right-2">
                        <XIcon width="1em" height="1em" />
                    </button>
                    <Link to="/cart">
                        <Button size="sm" className="px-4">
                            Checkout
                        </Button>
                    </Link>
                </div>
            </section>
        </div>
    );
}

function CremationPackageBanner({ slug, cart }) {
    const cartPackage = cart.packages[0];
    const [searchParams, setSearchParams] = useSearchParams();
    function closeBanner() {
        searchParams.delete("packageAdded");
        setSearchParams(searchParams, { preventScrollReset: true });
    }
    const redirectTo = `/packages/cremation/${slug}`;

    if (!cartPackage) {
        return null;
    }
    return (
        <div className="fixed bottom-10 left-1/2 -translate-x-1/2 max-w-2xl w-full z-50 px-2 md:px-0">
            <section className="flex justify-between w-full mx-auto px-5 py-3 bg-white shadow-2xl rounded-md border border-border">
                <section className="flex flex-col gap-2">
                    <section className="flex gap-6 items-center">
                        <h2 className="text-lg font-title">{cartPackage.salePackage.name}</h2>
                        <Link to={redirectTo} className="flex items-center gap-2 underline text-sm">
                            <EditIcon width="1em" height="1em" className="" />
                            Edit
                        </Link>
                    </section>
                    <p className="text-sm">Package added to cart</p>
                </section>
                <div className="flex flex-col items-end gap-2">
                    <button onClick={closeBanner}>
                        <XIcon width="1em" height="1em" />
                    </button>
                    <div className="font-medium text-xl">{currency(cartPackage.amounts.subtotal)}</div>
                </div>
            </section>
        </div>
    );
}

function SelectedFilters({ filters }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const filterKeys = Object.keys(filters).filter((key) => filters[key] !== "" && getFilterBadgeLabel(filters, key));

    if (filterKeys.length === 0) {
        return null;
    }

    // TODO: Use filterKeys
    return (
        <div className="flex gap-2 min-h-8 flex-wrap">
            {Object.keys(filters)
                // we display one badge for both minimumPrice and maximumPrice, so we don't need to include a badge for maximumPrice
                .filter((key) => filters[key] !== "")
                .map((key) => {
                    const label = getFilterBadgeLabel(filters, key);
                    if (!label) {
                        return null;
                    } else {
                        return (
                            <Badge variant="muted" className="bg-gray-200 shrink-0 text-sm" key={key}>
                                {label}
                                <CancelIcon
                                    onClick={() => {
                                        if (key === "minimumPrice") {
                                            searchParams.delete("minimumPrice");
                                            searchParams.delete("maximumPrice");
                                        } else {
                                            searchParams.delete(key);
                                        }
                                        setSearchParams(searchParams);
                                    }}
                                    width={12}
                                    height={12}
                                    className="text-black-primary hover:text-gray-800 hover:cursor-pointer ml-2"
                                />
                            </Badge>
                        );
                    }
                })}
        </div>
    );
}

const getFilterBadgeLabel = (params, key) => {
    const value = params[key];
    switch (key) {
        case "intermentRights":
            return `${value} interment right${value !== "1" ? "s" : ""}`;
        case "minimumPrice":
            return `Price $${params.minimumPrice} - $${params.maximumPrice}`;
        case "groundLevel":
            return value === "above-ground" ? "Above ground" : "Below ground";
        case "burialType":
            return value === "cremation" ? "Cremation" : "Burial";
        default:
            return null;
    }
};

const BELOW_GROUND_SPACES = ["LOTS"];
const ABOVE_GROUND_SPACES = ["MAUS", "NICHE"];

const CREMATION_BURIAL = [...BELOW_GROUND_SPACES, ...ABOVE_GROUND_SPACES];
const BURIAL_BURIAL = BELOW_GROUND_SPACES;

type FilterFunction = (space: Space, searchParamFilters: SearchParamFilters) => boolean;
export const matches: Record<string, FilterFunction> = {
    intermentRights: function (space, { intermentRights }) {
        // FIX: Issue with filtering HE properties, only valid when 4 IR defined
        return intermentRights === undefined || intermentRights <= space.intermentRights.length;
    },
    minimumPrice: function (space, { minimumPrice }) {
        return minimumPrice === undefined || space.price / 100 >= minimumPrice;
    },
    maximumPrice: function (space, { maximumPrice }) {
        return maximumPrice === undefined || space.price / 100 < maximumPrice;
    },
    groundLevel: function (space, { groundLevel }) {
        return (
            groundLevel === undefined ||
            groundLevel === "" ||
            (groundLevel === "below-ground" && BELOW_GROUND_SPACES.includes(space.propertyType)) ||
            (groundLevel === "above-ground" && ABOVE_GROUND_SPACES.includes(space.propertyType))
        );
    },
    burialType: function (space, { burialType }) {
        return (
            burialType === undefined ||
            burialType === "" ||
            (burialType === "cremation" && CREMATION_BURIAL.includes(space.propertyType)) ||
            (burialType === "burial" && BURIAL_BURIAL.includes(space.propertyType))
        );
    },
    mausoleumId: function (space, { mausoleumId }) {
        return mausoleumId === undefined || mausoleumId === "" || mausoleumId === space.lotRowNbrAlpha || mausoleumId === space.section;
    },
    crypt: function (space, { mausoleumId, crypt }) {
        const cryptInfo = cryptLocations[mausoleumId][crypt];

        return (
            crypt === undefined
                || crypt === ""
                || (mausoleumId === space.section) ? crypt === space.lotRowNbrAlpha : false
                    || (cryptInfo && space?.lotSpaceNbr) ? space?.lotSpaceNbr >= cryptInfo.min && space?.lotSpaceNbr <= cryptInfo.max : false
        );
    },
};

export function getRelatedSpaces(spaces: SpaceProps[], selected: SpaceProps): SpaceProps[] {
    return spaces.filter(
        ({ space_id, space_id_ref_1 }) => space_id !== selected.space_id && space_id_ref_1 === selected.space_id_ref_1,
    );
}

export async function action({ request }: ActionFunctionArgs) {
    const body = await request.formData();

    const intent = body.get("intent");
    const headers = new Headers();

    switch (intent) {
        case RESET_FILTERS: {
            return redirect("/map", { headers });
        }
        case APPLY_FILTERS: {
            const searchParams = new URLSearchParams();
            for (const [key, value] of body.entries()) {
                if (value) {
                    searchParams.set(key, value);
                }
            }
            return redirect(`/map?${searchParams.toString()}`, { headers });
        }
        case "add-to-cart": {
            const cart = await getOrCreateCartFromCookie({ request, headers });
            const lotSellUnitId = body.get("lotSellUnitId") as string;
            await addSpaceToCart(cart, { lotSellUnitId });

            return redirect(`/map?spaceId=${lotSellUnitId}&cartItemAdded=true`, { headers });
        }
        default: {
            return null;
        }
    }
}
